import React, { useState, useEffect } from 'react'
import {
  Search,
  Filter,
  Zap,
  Users,
  CheckCircle,
  X,
  ArrowRight,
  Star,
  Eye,
  Send,
  Sparkles,
  RefreshCw,
  AlertCircle
} from 'lucide-react'
import { mockCampaigns, mockCreators, formatCurrency, formatNumber } from '../../data/mockData'

const tierColors = {
  flagship: 'bg-purple-100 text-purple-700',
  macro: 'bg-blue-100 text-blue-700',
  mid_tier: 'bg-teal-100 text-teal-700',
  micro: 'bg-gray-100 text-gray-700',
}

function AdminMatching() {
  const [selectedMatch, setSelectedMatch] = useState(null)
  const [selectedCreators, setSelectedCreators] = useState([])
  const [campaigns, setCampaigns] = useState([])
  const [creators, setCreators] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [isSending, setIsSending] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  // Fetch campaigns in matching status and registered creators
  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    setIsLoading(true)
    setError('')

    try {
      // Fetch campaigns awaiting matching
      const campaignsRes = await fetch('/api/campaigns?status=matching')
      const campaignsData = await campaignsRes.json()

      // Fetch registered creators
      const creatorsRes = await fetch('/api/creators')
      const creatorsData = await creatorsRes.json()

      // Combine with mock data for demo purposes
      const realCampaigns = campaignsData.campaigns || []
      const allCreators = [...(creatorsData.creators || []), ...mockCreators]

      setCampaigns(realCampaigns)
      setCreators(allCreators)
    } catch (err) {
      console.error('Failed to fetch data:', err)
      setError('Failed to load data. Showing demo data.')
      // Use mock data as fallback
      setCreators(mockCreators)
    } finally {
      setIsLoading(false)
    }
  }

  const toggleCreator = (creatorId) => {
    setSelectedCreators(prev =>
      prev.includes(creatorId)
        ? prev.filter(id => id !== creatorId)
        : [...prev, creatorId]
    )
  }

  const handleSendInvitations = async () => {
    if (!selectedMatch || selectedCreators.length === 0) return

    setIsSending(true)
    setError('')
    setSuccess('')

    try {
      const response = await fetch('/api/invitations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          campaignId: selectedMatch.id,
          campaignName: selectedMatch.name,
          brandUserId: selectedMatch.brandUserId,
          brandName: selectedMatch.brandName,
          creatorUserIds: selectedCreators,
          budget: `$${formatNumber(selectedMatch.budget || 10000)}`,
          deadline: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          platforms: selectedMatch.brief?.platforms || ['instagram'],
          deliverables: selectedMatch.brief?.deliverableTypes || ['2 Posts'],
          brief: selectedMatch.brief?.coreMessage || 'Exciting campaign opportunity!',
        }),
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Failed to send invitations')
      }

      // Update campaign status
      await fetch('/api/campaigns', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          campaignId: selectedMatch.id,
          updates: { status: 'in_progress' },
        }),
      })

      setSuccess(`Invitations sent to ${selectedCreators.length} creator(s)!`)
      setSelectedMatch(null)
      setSelectedCreators([])
      fetchData() // Refresh data
    } catch (err) {
      setError(err.message || 'Failed to send invitations')
    } finally {
      setIsSending(false)
    }
  }

  // Get suggested creators for a campaign (combine real and mock)
  const getSuggestedCreators = (campaign) => {
    const brief = campaign.brief || {}
    const targetPlatforms = brief.platforms || []

    // Filter and score creators based on campaign requirements
    const scored = creators.map(creator => {
      let score = 85 // Base score

      // Platform match
      const creatorPlatforms = Object.keys(creator.platforms || {})
      const platformMatch = targetPlatforms.some(p =>
        creatorPlatforms.some(cp => cp.toLowerCase().includes(p.toLowerCase()))
      )
      if (platformMatch) score += 5

      // Verified bonus
      if (creator.verified) score += 5

      // High engagement bonus
      if (creator.avgEngagement > 5) score += 3

      // Cap at 99
      score = Math.min(score, 99)

      return { ...creator, matchScore: score }
    })

    // Sort by match score and return top 10
    return scored.sort((a, b) => b.matchScore - a.matchScore).slice(0, 10)
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 text-hf-teal-600 animate-spin" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Matching Queue</h1>
          <p className="text-gray-500">Review AI-suggested matches and send creator invitations</p>
        </div>
        <button onClick={fetchData} className="btn-primary">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </button>
      </div>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center">
          <AlertCircle className="w-5 h-5 mr-2" />
          {error}
        </div>
      )}

      {success && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm flex items-center">
          <CheckCircle className="w-5 h-5 mr-2" />
          {success}
        </div>
      )}

      {/* Pending Matches */}
      <div className="space-y-4">
        {campaigns.length === 0 ? (
          <div className="card text-center py-12">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Users className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No campaigns awaiting matching</h3>
            <p className="text-gray-500">When brands create new campaigns, they will appear here for creator matching.</p>
          </div>
        ) : (
          campaigns.map((campaign) => {
            const suggestedCreators = getSuggestedCreators(campaign)

            return (
              <div key={campaign.id} className="card">
                <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                  {/* Campaign Info */}
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-lg font-semibold text-gray-900">{campaign.name}</h3>
                      <span className="badge bg-purple-100 text-purple-700">Awaiting Match</span>
                    </div>
                    <p className="text-gray-500 mb-2">{campaign.brandName}</p>
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <span>Budget: {formatCurrency(campaign.budget || 0)}</span>
                      <span>Goal: {campaign.brief?.primaryGoal || 'Awareness'}</span>
                    </div>
                  </div>

                  {/* Suggested Creators Preview */}
                  <div className="flex items-center">
                    <div className="flex -space-x-2">
                      {suggestedCreators.slice(0, 4).map((creator) => (
                        <img
                          key={creator.id}
                          src={creator.avatar}
                          alt={creator.name}
                          className="w-10 h-10 rounded-full border-2 border-white object-cover"
                          title={creator.name}
                        />
                      ))}
                    </div>
                    <span className="ml-3 text-sm text-gray-500">
                      {suggestedCreators.length} suggested creators
                    </span>
                  </div>

                  {/* Actions */}
                  <button
                    onClick={() => {
                      setSelectedMatch({ ...campaign, suggestedCreators })
                      setSelectedCreators([])
                    }}
                    className="btn-primary"
                  >
                    Review Matches
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </button>
                </div>
              </div>
            )
          })
        )}
      </div>

      {/* Match Review Modal */}
      {selectedMatch && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-gray-900/50"
            onClick={() => setSelectedMatch(null)}
          />
          <div className="relative bg-white rounded-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b">
              <div>
                <h2 className="text-xl font-bold text-gray-900">{selectedMatch.name}</h2>
                <p className="text-gray-500">{selectedMatch.brandName} • {formatCurrency(selectedMatch.budget || 0)} budget</p>
              </div>
              <button
                onClick={() => setSelectedMatch(null)}
                className="p-2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Creator List */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-medium text-gray-900">AI-Suggested Creators</h3>
                <span className="text-sm text-gray-500">
                  {selectedCreators.length} selected
                </span>
              </div>

              <div className="space-y-4">
                {selectedMatch.suggestedCreators.map((creator) => {
                  const creatorId = creator.id || creator.userId
                  const isSelected = selectedCreators.includes(creatorId)

                  return (
                    <div
                      key={creatorId}
                      onClick={() => toggleCreator(creatorId)}
                      className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                        isSelected
                          ? 'border-hf-teal-500 bg-hf-teal-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-start gap-4">
                        {/* Checkbox */}
                        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-1 ${
                          isSelected ? 'bg-hf-teal-600 border-hf-teal-600' : 'border-gray-300'
                        }`}>
                          {isSelected && <CheckCircle className="w-4 h-4 text-white" />}
                        </div>

                        {/* Creator Info */}
                        <div className="flex-1">
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center gap-3">
                              <img
                                src={creator.avatar}
                                alt={creator.name}
                                className="w-12 h-12 rounded-full object-cover"
                              />
                              <div>
                                <div className="flex items-center gap-2">
                                  <h4 className="font-semibold text-gray-900">{creator.name}</h4>
                                  {creator.verified && (
                                    <CheckCircle className="w-4 h-4 text-hf-teal-600" />
                                  )}
                                  <span className={`badge ${tierColors[creator.tier] || tierColors.micro}`}>
                                    {(creator.tier || 'micro').replace('_', '-')}
                                  </span>
                                </div>
                                <p className="text-sm text-gray-500">{creator.title}</p>
                              </div>
                            </div>

                            {/* Match Score */}
                            <div className="text-center">
                              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-hf-teal-100 to-teal-100 flex items-center justify-center">
                                <span className="text-lg font-bold text-hf-teal-700">{creator.matchScore || 85}%</span>
                              </div>
                              <p className="text-xs text-gray-500 mt-1">Match</p>
                            </div>
                          </div>

                          {/* Stats */}
                          <div className="grid grid-cols-4 gap-4 mb-3">
                            <div>
                              <p className="text-sm font-medium text-gray-900">{formatNumber(creator.totalReach || 0)}</p>
                              <p className="text-xs text-gray-500">Reach</p>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-900">{(creator.avgEngagement || 0).toFixed(1)}%</p>
                              <p className="text-xs text-gray-500">Engagement</p>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-900">{creator.campaignsCompleted || 0}</p>
                              <p className="text-xs text-gray-500">Campaigns</p>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-900">{formatCurrency(creator.baseRate || 1000)}</p>
                              <p className="text-xs text-gray-500">Base Rate</p>
                            </div>
                          </div>

                          {/* Specialties */}
                          <div className="flex flex-wrap gap-1">
                            {(creator.specialty || []).slice(0, 3).map(spec => (
                              <span key={spec} className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">
                                {spec}
                              </span>
                            ))}
                          </div>

                          {/* AI Rationale */}
                          <div className="mt-3 p-3 bg-purple-50 rounded-lg">
                            <div className="flex items-start">
                              <Sparkles className="w-4 h-4 text-purple-600 mt-0.5 mr-2 flex-shrink-0" />
                              <p className="text-sm text-purple-900">
                                <span className="font-medium">AI Rationale:</span> {creator.verified ? 'Verified creator with' : 'Creator with'} strong audience engagement in healthcare.
                                {(creator.specialty || []).length > 0 && ` Specializes in ${(creator.specialty || []).slice(0, 2).join(' and ')}.`}
                                {creator.campaignsCompleted > 0 && ` ${creator.campaignsCompleted} successful campaigns completed.`}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-between p-6 border-t bg-gray-50">
              <div className="text-sm text-gray-500">
                {selectedCreators.length} creator{selectedCreators.length !== 1 ? 's' : ''} selected
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setSelectedMatch(null)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSendInvitations}
                  disabled={selectedCreators.length === 0 || isSending}
                  className="btn-primary disabled:opacity-50"
                >
                  {isSending ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Send Invitations ({selectedCreators.length})
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AdminMatching
